/*
 * Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
 * This software is the proprietary information of Irian Software Development SRL.
 * Use is subject to license and non-disclosure terms
 */

import {Injectable} from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {UserModel} from './user/user.model';
import {Observable} from 'rxjs';
import {EnvironmentService} from "@irian-ro/util-environment";
import {LazyLoadEvent} from "primeng/api";
import { AbstractResourceService, UrlBuilderService } from '@irian-ro/abstract-resource';
import {AbstractSaveUserService} from "./abstract-save-user.service";
import {UserPasswordChangeDtoModel} from "./user/user-password-change-dto.model";
import { UserRemainingBlockTimeVo } from './user/user-remaining-block-time-vo';

@Injectable({
  providedIn: 'root'
})
export class UsersService extends AbstractResourceService<any, any, any, any> implements AbstractSaveUserService {
  header: HttpHeaders | undefined = undefined;

  constructor(httpClient: HttpClient,
              environmentService: EnvironmentService) {
    super(
      httpClient,
      environmentService.getEnvironmentProperty('usersUrl'),
      null,
      null
    );
  }

  getUsers(event: LazyLoadEvent): Observable<UserModel[]> {
    return <Observable<UserModel[]>>this.httpClient?.get(
      UrlBuilderService.createUrlFromLazyLoadEvent(this.url, event));
  }

  getUserById(uuid: string): Observable<UserModel> {
    return <Observable<UserModel>>this.httpClient?.get(`${this.url}/details/${uuid}`);
  }

  getNewUser(): Observable<UserModel> {
    return <Observable<UserModel>>this.httpClient?.get(`${this.url}/newUser`);
  }

  saveUserRights(userModel: UserModel): Observable<any> {
    return this.httpClient?.post(
      `${this.url}/details`,
      userModel);
  }

  saveUserProfile(userModel: UserModel): Observable<any> {
    return this.httpClient?.put(
      `${this.url}/user-profile`,
      userModel);
  }

  deleteUser(uuid: string): Observable<any> {
    return this.httpClient?.put(
      `${this.url}/delete/${uuid}`, uuid);
  }

  activateUser(uuid: string): Observable<any> {
    return this.httpClient?.put(
      `${this.url}/activate/${uuid}`, null);
  }

  resendEmailConfirmation(mainCredential: string): Observable<any> {
    return this.httpClient?.post(
      `${this.url}/resend-confirmation-email/${mainCredential}`, mainCredential);
  }

  passwordRecoveryRequest(mainCredential: string): Observable<any> {
    return this.httpClient?.post(
      `${this.url}/password-recovery-request/${mainCredential}`, null, {
        responseType: 'text'
      });
  }

  saveCompanySuperUser(userModel: any) {
    return this.httpClient?.post(`${this.url}/company-super-user`, userModel);
  }

  saveNewUserFromSignup(userModel: UserModel) {
    return this.httpClient?.post(`${this.url}/signup`, userModel);
  }

  assignRightsToCompanySuperUser(userModel: any) {
    return this.httpClient?.post(`${this.url}/assign-rights`, userModel);
  }

  getAllRights() {
    return this.httpClient?.get(`${this.url}/all-rights`);
  }

  savePasswordUpdate(userModel: UserModel) {
    return this.httpClient?.put(
      `${this.url}/recover/password/${userModel.uuid}`,
      userModel);
  }

  changePassword(userPasswordChangeDtoModel: UserPasswordChangeDtoModel) {
    return this.httpClient?.put(`${this.url}/change-user-password`, userPasswordChangeDtoModel);
  }

  getRemainingBlockTimeOfUser(username: string): Observable<UserRemainingBlockTimeVo> {
    return <Observable<UserRemainingBlockTimeVo>>this.httpClient?.get(`${this.url}/remaining-block-time/${username}`);
  }
}
